// @flow

import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { ArrowLightIcon } from '@riseart/icons';
import {
  GridPaginationWrapper,
  GridCounter,
  GridHeader,
  GridSortSelect,
  GridLayoutSwitch,
} from '@riseart/grid';
import { Section, SectionContent, Wrapper, wrapperPaddings } from '@riseart/layout';
import { Link, SliderPagination, scrollToElement } from '@riseart/common';
import { art as ART_ENUM, cart as CART_ENUM } from 'Enum';
import { useQuery } from 'shared_services/apollo/useQuery';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import FILTER_COLLECTION_ART_QUERY from 'shared_data/queries/filter/collectionArt.graphql';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { SliderPaginationCounter } from 'shared_components/common/pagination/counter/Slider';
import { ArtGridFluid } from 'shared_components/arts/containers/grid/Fluid';
import { paginationItemLinkCls } from '@riseart/common/es/pagination/Slider.less';

type Props = {
  collectionId: number,
  title: string,
  subTitle?: string,
  primaryAction?: 'buy' | 'rent',
  hidePrices?: boolean,
  itemsPerPage?: number,
  initialPage?: number,
  cardDisplayStyle?: number,
  disableHeader?: boolean,
  layout?: string,
};

/**
 * CmsModuleCollectionArt
 */
export function CmsModuleCollectionArt({
  collectionId,
  title,
  subTitle,
  primaryAction = 'buy',
  hidePrices = false,
  itemsPerPage = 30,
  initialPage = 1,
  cardDisplayStyle = 1,
  disableHeader = false,
  layout: defaultLayout = ART_ENUM.list.LAYOUT_FLUID,
}: Props): Node {
  const { formatMessage } = useIntl();
  const store = useStoreCode();
  const sectionContentRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [sort, setSort] = useState(ART_ENUM.rank.RANKING_DEFAULT);
  const [layout, setLayout] = useState(
    defaultLayout && Object.keys(ART_ENUM.list).some((key) => ART_ENUM.list[key] === defaultLayout)
      ? defaultLayout
      : ART_ENUM.list.LAYOUT_FLUID,
  );

  // Custom page change to update the currentPage state
  const handlePageChange = useCallback(
    (newPage: number) => (event: Event) => {
      event.preventDefault();
      setCurrentPage(newPage);
      scrollToElement(sectionContentRef);
    },
    [setCurrentPage],
  );

  // Load data
  const { data, loading } = useQuery(FILTER_COLLECTION_ART_QUERY, {
    ssr: false,
    skip: !collectionId,
    variables: {
      collectionId,
      inputArtFilter: {
        items: itemsPerPage,
        page: currentPage,
        sort,
        store,
      },
    },
  });

  const { items = [], pagination = {} } = (data && data.filterCollectionArt) || {};
  const currentPageFromApi = (pagination && pagination.currentPage) || null;

  // Update the currentPage only if the pagination is loaded from server
  // and page is different than currentPage state
  useEffect(() => {
    if (currentPage && currentPageFromApi && currentPage !== currentPageFromApi) {
      setCurrentPage(currentPageFromApi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageFromApi]);

  const sortList = useRef(
    Object.keys(ART_ENUM.rank).filter(
      (rank) => ART_ENUM.rank[rank] !== ART_ENUM.rank.RANKING_RELEVANCE,
    ),
  );
  const sortOptions = useMemo(
    () =>
      sortList.current.map((key) => ({
        label: formatMessage({ id: `options.sort.art.${ART_ENUM.rank[key]}` }),
        value: ART_ENUM.rank[key],
        url: null,
        selected: sort === ART_ENUM.rank[key],
      })),
    [formatMessage, sort, sortList],
  );
  const layoutOptions = useMemo(
    () =>
      Object.keys(ART_ENUM.list).map((key) => ({
        label: formatMessage({ id: `options.layout.${ART_ENUM.list[key]}` }),
        value: ART_ENUM.list[key],
        url: null,
        selected: layout === ART_ENUM.list[key],
      })),
    [formatMessage, layout],
  );

  return collectionId ? (
    <Section>
      <Wrapper>
        <SectionContent
          title={title}
          subTitle={subTitle}
          headingProps={{ tag: 'h2', level: 3, align: 'center', uppercase: true }}
          headingSubTitleProps={{ tag: 'h3', align: 'center' }}
        >
          <IsomorphicRipple isActive={loading}>
            <span ref={sectionContentRef} />
            {!disableHeader ? (
              <GridHeader>
                <GridSortSelect
                  label={formatMessage({ id: 'components.art.sort' })}
                  onChange={setSort}
                  options={sortOptions}
                />
                <div>
                  {pagination.currentPage ? (
                    <GridCounter>
                      <SliderPaginationCounter
                        currentPage={pagination.currentPage}
                        itemsPerPage={pagination.itemsPerPage}
                        totalItems={pagination.totalItems}
                      />
                    </GridCounter>
                  ) : null}
                  <GridLayoutSwitch options={layoutOptions} onClick={setLayout} />
                </div>
              </GridHeader>
            ) : null}
            <ArtGridFluid
              loading={loading || !store}
              items={items}
              imageType={layout}
              wrapperPaddings={wrapperPaddings}
              cardDisplayStyle={cardDisplayStyle}
              cardProps={{ showPrice: !hidePrices }}
              showAddToCart={primaryAction === CART_ENUM.type.TYPE_BUY}
              showRent={primaryAction === CART_ENUM.type.TYPE_RENT}
            />
            {items && items.length ? (
              <GridPaginationWrapper>
                <SliderPagination
                  current={currentPage}
                  total={pagination.totalItems}
                  totalPages={pagination.totalPages}
                  pageSize={itemsPerPage}
                  itemRenderer={(
                    page: number,
                    type: 'page' | 'prev' | 'next',
                    disabled: boolean = false,
                  ): Node => {
                    const url = '';

                    switch (type) {
                      case 'page': {
                        return (
                          <Link
                            onClick={handlePageChange(page)}
                            to={url}
                            title={`${formatMessage({ id: 'components.pagination.page' })} ${page}`}
                            className={paginationItemLinkCls}
                            disabled={disabled}
                            obfuscate
                            obflinkProps={{ stopPropagation: true }}
                          >
                            {page}
                          </Link>
                        );
                      }
                      case 'prev': {
                        return (
                          <Link
                            onClick={handlePageChange(page)}
                            to={url}
                            title={formatMessage({ id: 'components.pagination.previous' })}
                            className={paginationItemLinkCls}
                            disabled={disabled}
                            obfuscate
                            obflinkProps={{ stopPropagation: true }}
                          >
                            <ArrowLightIcon />
                          </Link>
                        );
                      }
                      case 'next': {
                        return (
                          <Link
                            onClick={handlePageChange(page)}
                            to={url}
                            title={formatMessage({ id: 'components.pagination.next' })}
                            className={paginationItemLinkCls}
                            disabled={disabled}
                            obfuscate
                            obflinkProps={{ stopPropagation: true }}
                          >
                            <ArrowLightIcon />
                          </Link>
                        );
                      }
                      default: {
                        return null;
                      }
                    }
                  }}
                />
              </GridPaginationWrapper>
            ) : null}
          </IsomorphicRipple>
        </SectionContent>
      </Wrapper>
    </Section>
  ) : null;
}
